import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { flowFetch, FlowTask, FlowArea, FlowUser, STATUS_LABELS, STATUS_COLORS, PRIORITY_COLORS, PRIORITY_LABELS, formatDate, formatDuration, isOverdue } from './flowApi';
import { TaskDetailModal } from './FlowTasks';

// Painel de tarefas diárias por dia (dashboard do admin)
function DailyTasksDayCard({ areas, users, onOpen }: { areas: FlowArea[]; users: FlowUser[]; onOpen: (id: string) => void }) {
  const [date, setDate] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  void areas; void users;

  const load = async () => {
    setLoading(true);
    try {
      setData(await flowFetch(`/api/flow/daily-overview?date=${date}`));
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [date]);

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-violet-500">Tarefas diárias</p>
          {data && <p className="text-sm font-semibold text-slate-700">{data.done} de {data.total} concluídas {data.isToday ? 'hoje' : 'neste dia'}</p>}
        </div>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
      </div>
      {loading ? (
        <div className="flex justify-center py-6"><div className="h-5 w-5 animate-spin rounded-full border-b-2 border-violet-500"></div></div>
      ) : !data || data.items.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">Nenhuma tarefa diária neste dia.</p>
      ) : (
        <ul className="space-y-1.5">
          {data.items.map((it: any) => (
            <li key={it.taskId}
              onClick={() => data.isToday && onOpen(it.taskId)}
              className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${data.isToday ? 'cursor-pointer hover:bg-slate-50' : ''}`}>
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${it.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                {it.completed ? '✓' : '·'}
              </span>
              <div className="min-w-0 flex-1">
                <p className={`truncate text-sm ${it.completed ? 'text-slate-800' : 'text-slate-500'}`}>{it.title}</p>
                <p className="truncate text-[11px] text-slate-400">
                  {it.area?.name || 'Sem área'}{it.assignee?.nome ? ` · ${it.assignee.nome}` : ''}
                </p>
              </div>
              <span className="shrink-0 text-xs text-slate-400">{formatDuration(it.timeSpentSeconds)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TaskMiniList({ tasks, empty, onOpen }: { tasks: FlowTask[]; empty: string; onOpen: (id: string) => void }) {
  if (!tasks.length) return <p className="text-sm text-slate-400">{empty}</p>;
  return (
    <ul className="space-y-1.5">
      {tasks.slice(0, 8).map(t => (
        <li key={t.id} onClick={() => onOpen(t.id)}
          className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50">
          <span className="min-w-0 truncate text-sm text-slate-800">{t.title}</span>
          <span className="flex shrink-0 items-center gap-1.5">
            {t.dueDate && <span className={`text-xs ${isOverdue(t) ? 'font-bold text-red-600' : 'text-slate-400'}`}>{formatDate(t.dueDate)}</span>}
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${PRIORITY_COLORS[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Card({ title, children, tone }: { title: string; children: React.ReactNode; tone?: string }) {
  return (
    <div className={`rounded-2xl border bg-white p-4 shadow-sm ${tone || 'border-slate-200'}`}>
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">{title}</p>
      {children}
    </div>
  );
}

export function FlowDashboard({ areas, users }: { areas: FlowArea[]; users: FlowUser[] }) {
  const [data, setData] = useState<any>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const load = async () => {
    try {
      setData(await flowFetch('/api/flow/dashboard'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar dashboard');
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>;

  const u = data.user;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Card title="Minhas tarefas de hoje"><TaskMiniList tasks={u.today} empty="Nada com prazo para hoje 🎉" onOpen={setDetailId} /></Card>
        <Card title="Tarefas atrasadas" tone={u.overdue.length ? 'border-red-200' : 'border-slate-200'}>
          <TaskMiniList tasks={u.overdue} empty="Nenhuma tarefa atrasada" onOpen={setDetailId} />
        </Card>
        <Card title="Em andamento (Fazendo)"><TaskMiniList tasks={u.doing} empty="Nada em andamento" onOpen={setDetailId} /></Card>
        <Card title="Aguardando"><TaskMiniList tasks={u.waiting} empty="Nada aguardando" onOpen={setDetailId} /></Card>
        <Card title="Delegadas por mim"><TaskMiniList tasks={u.delegated} empty="Você não delegou tarefas abertas" onOpen={setDetailId} /></Card>
        <Card title="Próximos compromissos">
          {u.upcoming.length === 0 ? <p className="text-sm text-slate-400">Nenhum compromisso futuro</p> : (
            <ul className="space-y-1.5 text-sm">
              {u.upcoming.map((e: any) => (
                <li key={e.id} className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-slate-800">
                    {e.type === 'MEETING' && <span className="mr-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">Reunião</span>}
                    {e.title}{e.type === 'MEETING' && e.contactName ? <span className="text-slate-500"> · {e.contactName}</span> : null}
                  </span>
                  <span className="text-xs text-slate-400">{formatDate(e.startAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Comentários onde fui marcado">
          {u.mentions.length === 0 ? <p className="text-sm text-slate-400">Nenhuma menção não lida</p> : (
            <ul className="space-y-1.5 text-sm">
              {u.mentions.map((n: any) => (
                <li key={n.id} className="cursor-pointer text-slate-700 hover:text-cyan-700"
                  onClick={() => n.taskId && setDetailId(n.taskId)}>
                  {n.message} <span className="text-xs text-slate-400">· {formatDate(n.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {data.isAdmin && data.admin && (
        <div>
          <h2 className="mb-3 text-lg font-bold text-slate-900">Visão do administrador</h2>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-3 text-center shadow-sm">
                <p className="text-2xl font-black text-slate-900">{data.admin.byStatus[k] || 0}</p>
                <p className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[k]}`}>{v}</p>
              </div>
            ))}
            <div className="rounded-xl border border-emerald-200 bg-white p-3 text-center shadow-sm">
              <p className="text-2xl font-black text-emerald-600">{data.admin.doneToday}</p>
              <p className="mt-1 text-[10px] font-semibold text-slate-500">Concluídas hoje</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-center shadow-sm">
              <p className="text-2xl font-black text-slate-900">{data.admin.noDue}</p>
              <p className="mt-1 text-[10px] font-semibold text-slate-500">Sem prazo</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-center shadow-sm">
              <p className="text-2xl font-black text-slate-900">{data.admin.noAssignee}</p>
              <p className="mt-1 text-[10px] font-semibold text-slate-500">Sem responsável</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card title="Tarefas atrasadas da empresa" tone={data.admin.overdue.length ? 'border-red-200' : undefined}>
              <TaskMiniList tasks={data.admin.overdue} empty="Nenhuma atrasada 🎉" onOpen={setDetailId} />
            </Card>
            <Card title="Abertas por responsável">
              <ul className="space-y-1 text-sm">
                {data.admin.byAssignee.map((g: any, i: number) => (
                  <li key={i} className="flex justify-between"><span className="text-slate-700">{g.user?.nome || 'Sem responsável'}</span><span className="font-bold text-slate-900">{g.count}</span></li>
                ))}
              </ul>
            </Card>
            <Card title="Abertas por área">
              <ul className="space-y-1 text-sm">
                {data.admin.byArea.map((g: any, i: number) => (
                  <li key={i} className="flex justify-between"><span className="text-slate-700">{g.area?.name || 'Sem área'}</span><span className="font-bold text-slate-900">{g.count}</span></li>
                ))}
              </ul>
            </Card>
            <Card title="Abertas por quadro">
              <ul className="space-y-1 text-sm">
                {(data.admin.byBoard || []).map((g: any, i: number) => (
                  <li key={i} className="flex justify-between"><span className="text-slate-700">{g.board?.name || 'Sem quadro'}</span><span className="font-bold text-slate-900">{g.count}</span></li>
                ))}
              </ul>
            </Card>
            <Card title="Últimas atividades">
              <ul className="space-y-1 text-xs text-slate-500">
                {data.admin.activities.map((a: any) => (
                  <li key={a.id}><span className="font-semibold text-slate-700">{a.user?.nome || '—'}</span> {a.message} · {formatDate(a.createdAt)}</li>
                ))}
              </ul>
            </Card>
            <DailyTasksDayCard areas={areas} users={users} onOpen={setDetailId} />
          </div>
        </div>
      )}

      {detailId && <TaskDetailModal taskId={detailId} areas={areas} users={users} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}
